// TIPSSSO (self-hosted ZITADEL) login portal integration for Tips Contact (CRM).
//
// The SPA uses OIDC Authorization Code + PKCE against the central ZITADEL issuer
// (`sso.tipsdata.co`). Every value here is PUBLIC — a public SPA client carries no
// secret — so they are safe to bake into the bundle at build time.
//
// When the issuer/client id are absent (e.g. before a ZITADEL application is
// provisioned for tipscontact.com) the portal is "not configured" and the UI falls
// back to the legacy email/password login.
import { UserManager, WebStorageStateStore, type UserManagerSettings } from 'oidc-client-ts';

const issuer = import.meta.env.VITE_ZITADEL_ISSUER ?? '';
const clientId = import.meta.env.VITE_ZITADEL_CLIENT_ID ?? '';
// The ZITADEL project id (number) for this app. When present, the SPA requests the
// project-audience reserved scope so the project id is asserted into the access
// token's `aud` — which is what the backend pins as ZITADEL_AUDIENCE.
const projectId = import.meta.env.VITE_ZITADEL_PROJECT_ID ?? '';

// Additional ZITADEL project ids whose granted roles must also be asserted into THIS
// app's access token (comma-separated). ZITADEL only emits a project's roles in the
// `urn:zitadel:iam:org:project:roles` claim for projects the token is audienced to, so
// to read cross-project roles (e.g. the `sales.*` roles that live in the tips-platform
// project, used to gate lead distribution) we request that project's audience scope too.
// Ships dormant (no extra scope) until set. Example: VITE_ZITADEL_EXTRA_PROJECT_IDS=378...
const extraProjectIds: string[] = String(import.meta.env.VITE_ZITADEL_EXTRA_PROJECT_IDS ?? '')
  .split(',')
  .map((s) => s.trim())
  .filter((id) => Boolean(id) && id !== projectId);

// The CRM bundle is served on several domains (crm.databaseemailer.app,
// crm.databaseemailer.com, crm.dataleadservice.com, tipscontact.com). The ZITADEL
// portal login + the TipsContact-branded landing are scoped to tipscontact.com (the
// domains whose /auth/callback is registered on the ZITADEL app); every other CRM
// domain keeps the legacy password login and its own branding.
const SSO_HOSTS = ['tipscontact.com', 'www.tipscontact.com', 'localhost', '127.0.0.1'];
export const isSsoHost = (): boolean =>
  typeof window !== 'undefined' && SSO_HOSTS.includes(window.location.hostname);

// Origin-based redirect so each registered SSO host returns to itself (PKCE state is
// stored per-origin — a fixed cross-origin redirect would break the callback).
const redirectUri = `${window.location.origin}/auth/callback`;

/** True only when configured AND on an SSO host (portal login is offered). */
export const oidcEnabled = Boolean(issuer && clientId && isSsoHost());

// project-roles claim + (when known) this app's project id in the token audience, plus
// any additional projects whose roles must be asserted (e.g. tips-platform for sales.*).
const scope = [
  'openid',
  'profile',
  'email',
  'urn:zitadel:iam:org:project:roles',
  projectId ? `urn:zitadel:iam:org:project:id:${projectId}:aud` : '',
  ...extraProjectIds.map((id) => `urn:zitadel:iam:org:project:id:${id}:aud`),
]
  .filter(Boolean)
  .join(' ');

const settings: UserManagerSettings = {
  authority: issuer,
  client_id: clientId,
  redirect_uri: redirectUri,
  post_logout_redirect_uri: `${window.location.origin}/welcome`,
  silent_redirect_uri: `${window.location.origin}/auth/silent-renew`,
  response_type: 'code',
  scope,
  automaticSilentRenew: true,
  loadUserInfo: false,
  monitorSession: false,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
};

// Constructed only when configured; callers must guard on `oidcEnabled`.
export const userManager: UserManager | null = oidcEnabled ? new UserManager(settings) : null;

/**
 * Begin the login redirect to the TIPSSSO portal. `returnTo` is round-tripped
 * through the OIDC `state` so the callback can land the user back where they
 * intended. Falls back to the legacy login page when the portal is not configured.
 */
export async function startSsoLogin(returnTo = '/'): Promise<void> {
  if (userManager) {
    await userManager.signinRedirect({ state: { returnTo } });
  } else {
    window.location.assign('/login');
  }
}

/** Clear the local OIDC session and redirect through the ZITADEL end-session flow. */
export async function ssoLogout(): Promise<void> {
  if (!userManager) return;
  try {
    await userManager.signoutRedirect();
  } catch {
    await userManager.removeUser();
    window.location.assign('/welcome');
  }
}
